import { css } from '@emotion/react'
import clsx from 'clsx'
import { pipe } from 'effect'
import * as O from 'effect/Option'
import { observer, useLocalObservable } from 'mobx-react'

import { useStudentAppMediaQuery } from '~/@common/hooks/useMediaQuery'
import { useRepository } from '~/@common/hooks/useRepository.ts'
import { commonRepo } from '~/@common/services/repo.service'
import { colors, fontWeight, textEllipsis } from '~/@common/styles'
import { mediaQuery } from '~/@common/styles/mediaQuery'
import { ChallengeService } from '~/@pages/student/challenge/@widget/Challenge.service.ts'
import ConceptChipOfLittleChapter from '~/@pages/student/challenge/@widget/ConceptChipOfLittleChapter/ConceptChipOfLittleChapter.tsx'

export const kind = 'ms__ConceptChipOfMiddleChapter'

export type Props = {
  middleChapterId: number
  selectedMiddleChapterId: number | null
}

const ConceptChipOfMiddleChapter = ({ middleChapterId, selectedMiddleChapterId }: Props) => {
  const service = useRepository(ChallengeService)
  const { isMobile } = useStudentAppMediaQuery()
  const hasBorder = commonRepo.isElementarySchoolflat && !isMobile

  const localStore = useLocalObservable(() => ({
    get firstItem() {
      const middleChapterData = service.getIdFromTag('middleChapterId', middleChapterId)

      return pipe(
        middleChapterData,
        O.flatMap((ids) => service.get(ids.values().next().value)),
      )
    },

    get littleChapterIds() {
      const tagName = `중단원/${middleChapterId}` as const
      return pipe(
        service.getFilteredIdMapFromKindTag(tagName),
        O.flatMap((ids) => {
          if (ids.size === 0) return O.none()
          return O.some([...ids.keys()])
        }),
      )
    },
  }))

  if (O.isNone(localStore.firstItem)) return null
  if (O.isNone(localStore.littleChapterIds)) return null

  return (
    <details
      css={_css}
      data-middle-chapter-id={middleChapterId}
      className={selectedMiddleChapterId === middleChapterId ? 'selected' : ''}
      {...(closedMiddleChapterIdSet.has(middleChapterId) ? null : { open: true })}
    >
      <summary role="button" onClick={() => toggleClosedMiddleChapterId(middleChapterId)}>
        <b>
          {localStore.firstItem.value.value.bigChapterName} ㅣ&nbsp;
          {localStore.firstItem.value.value.middleChapterName}
        </b>
      </summary>
      <div className={clsx('chapter-contents', hasBorder && 'border')}>
        {localStore.littleChapterIds.value.map((id) => (
          <ConceptChipOfLittleChapter
            key={`${id}`}
            middleChapterId={middleChapterId}
            littleChapterId={id}
          />
        ))}
      </div>
    </details>
  )
}

const openedMiddleChapterIdSessionKey = `${kind}/openedMiddleChapterId`

const sessionOpenedMiddleChapter = sessionStorage.getItem(openedMiddleChapterIdSessionKey)

const closedMiddleChapterIdSet = new Set(sessionOpenedMiddleChapter?.split(',').map(Number) ?? [])

const toggleClosedMiddleChapterId = (middleChapterId: number) => {
  if (closedMiddleChapterIdSet.has(middleChapterId)) {
    closedMiddleChapterIdSet.delete(middleChapterId)
  } else {
    closedMiddleChapterIdSet.add(middleChapterId)
  }
  sessionStorage.setItem(openedMiddleChapterIdSessionKey, [...closedMiddleChapterIdSet].join(','))
}

export default observer(ConceptChipOfMiddleChapter)

const _css = css`
  width: 100%;
  border-radius: var(--Radius-300);
  overflow: hidden;
  margin-top: 10px;

  &.selected {
    border: 2px solid ${colors.blue.$500};
  }

  &:first-of-type {
    margin-top: 0;
  }

  summary {
    padding: 12px 20px;
    border-radius: var(--Radius-300);
    background: #e1e6eb;

    font-weight: ${fontWeight.bold};
  }

  summary b {
    margin-left: 6px;
    ${textEllipsis(2)};
  }

  &[open] summary {
    border-radius: var(--Radius-300) var(--Radius-300) 0 0;
  }

  .chapter-contents {
    display: flex;
    flex-direction: column;
    padding: 14px;
    gap: 14px;
    content-visibility: auto;
    background: ${colors.white};

    &.border {
      border: 1px solid var(--Colors-Gray-300);
      border-radius: 0 0 var(--Radius-300) var(--Radius-300);
    }
  }

  &[open] .chapter-contents {
    content-visibility: visible;
  }

  ${mediaQuery.underTablet} {
    .chapter-contents {
      padding: 0;
      gap: 4px;
      background-color: #f0f2f4;
    }
  }
`
